import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Card, Alert, Button } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { toast } from 'react-toastify';
import { resetErrore } from 'features/labels/labelsSlice';

import HtmlHead from 'components/html-head/HtmlHead';
import Loading from 'components/loading/Loading';
import ExcelExport from 'components/export/ExcelExport';
import CsLineIcons from 'cs-line-icons/CsLineIcons';

import { richiediLabels, getFileLabel } from './funzioni';

const { SearchBar } = Search;

// Funzione per generare le colonne dinamicamente dai dati
const getColumns = (data, onRunClick) => {
  if (!data || data.length === 0) {
    return [];
  }

  const keys = Object.keys(data[0]);

  const columns = keys.map((key) => ({
    dataField: key,
    text: key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim(),
    sort: true,
  }));

  // Aggiungi colonna per il pulsante Run
  columns.push({
    dataField: 'run',
    text: 'Action',
    sort: false,
    headerStyle: { textAlign: 'center', width: '100px' },
    style: { textAlign: 'center' },
    formatter: (cell, row) => {
      // Cerca Ordine con diverse varianti di case
      const ordine = row.Ordine;
      // Cerca codarticolo con diverse varianti di case
      const codarticolo = row.CodArticolo;

      if (!ordine || !codarticolo) {
        return <span className="text-muted">N/A</span>;
      }

      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => onRunClick(ordine, codarticolo)}
          className="d-flex align-items-center mx-auto"
        >
          <CsLineIcons icon="eye" className="me-1" />
          Open Label
        </Button>
      );
    },
  });

  return columns;
};

const Labels = () => {
  const title = 'Labels';
  const description = 'Manage and print labels';

  const { caricamentoInCorso, labels, errore } = useSelector((s) => s.labels);
  const dispatch = useDispatch();

  const labelsWithId = useMemo(() => {
    return labels.map((l, i) => ({ ...l, _id: `row-${i}` }));
  }, [labels]);

  useEffect(() => {
    richiediLabels();
  }, []);

  // Mostra errori con toast notification
  useEffect(() => {
    if (errore) {
      toast.error(errore, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [errore]);

  const handleRunClick = (ordine, codarticolo) => {
    getFileLabel(ordine, codarticolo);
  };

  // Genero le colonne dinamicamente dai risultati
  const columns = getColumns(labels, handleRunClick);

  if (caricamentoInCorso) {
    return (
      <>
        <HtmlHead title={title} description={description} />
        <Loading />
      </>
    );
  }

  return (
    <>
      <HtmlHead title={title} description={description} />
      <Row>
        <Col>
          <section className="scroll-section" id="title">
            <div className="page-title-container">
              <h1 className="mb-0 pb-0 display-4">{title}</h1>
            </div>
          </section>

          <section className="scroll-section">
            <Card body className="mb-3">
              {errore && (
                <Alert variant="danger" onClose={() => dispatch(resetErrore())} dismissible className="mb-3">
                  <Alert.Heading>Errore</Alert.Heading>
                  <p className="mb-0">{errore}</p>
                </Alert>
              )}
            </Card>
          </section>

          <section className="scroll-section mb-3">
            <ExcelExport idTabella="tabella-labels" />
          </section>

          <section className="scroll-section">
            <Card body>
              {columns.length > 0 ? (
                <ToolkitProvider keyField="_id" data={labelsWithId} columns={columns} search>
                  {(props) => (
                    <>
                      <div className="mb-2">
                        <SearchBar {...props.searchProps} />
                      </div>
                      <BootstrapTable
                        striped
                        id="tabella-labels"
                        bootstrap4
                        condensed
                        wrapperClasses="table-responsive"
                        {...props.baseProps}
                        noDataIndication="No labels available"
                      />
                    </>
                  )}
                </ToolkitProvider>
              ) : (
                <div className="text-center p-4">
                  <p className="text-muted">No labels available</p>
                </div>
              )}
            </Card>
          </section>
        </Col>
      </Row>
    </>
  );
};

export default Labels;
